import React, { useState } from "react"
import { useDispatch } from "react-redux"
import Head from "next/head"
import { useSpring, animated } from "react-spring"
import { Waypoint } from "react-waypoint"

import { Container, Card, Col, Row, Button } from "react-bootstrap"

import {
    toggleMakeEnquiryModal,
    setMakeEnquiryReqType,
} from "@/slices/makeEnquiryModalSlice"

import CustomImage from "@/main/customImage"

import MiscHeroImage from "@/main/miscHeroImage"
import ServiceCard from "@/services/servicesCard"

function ServicesScreen() {
    const dispatch = useDispatch()

    const url = "https://webbjenkins.co.uk/property-services/"
    const title = "Property Services Isle of Wight - Webb Jenkins"
    const description =
        "Get in touch today to arrange a free quote. Our local expert tradesmen offer a range of property maintenance services across the Isle of Wight."
    const imgUrl =
        "https://webb-jenkins-static-164679926.imgix.net/socials/webbjenkins-services-social.jpg"
    const imgWidth = 1200
    const imgHeight = 630
    const imgAlt = "Webb Jenkins logo with property services caption"

    const [inView, setInView] = useState(false)

    const fadeLeft = useSpring({
        delay: 500,
        to: {
            opacity: inView ? 1 : 0,
            marginRight: inView ? 0 : 300,
        },
    })

    return (
        <>
            <Head>
                <link rel="canonical" href={url} />
                <title>{title}</title>
                <meta name="description" content={description} />

                <meta property="og:title" content={title} />
                <meta property="og:image:url" content={imgUrl} />
                <meta property="og:image:width" content={imgWidth} />
                <meta property="og:image:height" content={imgHeight} />
                <meta property="og:image:alt" content={imgAlt} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image:src" content={imgUrl} />
                <meta name="twitter:site" content="@WebbJenkinsIOW" />
            </Head>
            <MiscHeroImage
                title="Property Services"
                subtitle="Using quality tradesmen is vital if you want a job done to the highest standard. Our team of experienced professionals won't let you down"
                image="tradesman-measuring-job.jpg"
                alt="Professional tradesman measuring up a wall for a maintenance job"
                position="center"
                buttonText="Make enquiry"
                buttonOnClick={() => {
                    dispatch(toggleMakeEnquiryModal(true))
                    dispatch(setMakeEnquiryReqType("general"))
                }}
            />

            <Container fluid="md" as={Card} className="px-0 card-overlap-2">
                <Card.Body>
                    <Card.Title>
                        <h2 className="text-center">Services we offer</h2>
                    </Card.Title>
                </Card.Body>
            </Container>
            <Container fluid="md" className="px-0 mt-4">
                <Row className="mx-0">
                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 pe-sm-2 pe-lg-4"
                    >
                        <ServiceCard
                            icon="rennovation-icon.svg"
                            alt="Rebuild of a house"
                            title="Complete renovations"
                        />
                    </Col>

                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 ps-sm-2 ps-lg-2 pe-lg-3"
                    >
                        <ServiceCard
                            icon="plumbing-icon.svg"
                            alt="Mains pipe"
                            title="Plumbing & heating"
                        />
                    </Col>
                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className=" px-0 mb-4 pe-sm-2 pe-lg-2 ps-lg-3"
                    >
                        <ServiceCard
                            icon="gardening-icon.svg"
                            alt="Groundworks"
                            title="Gardening & groundworks"
                        />
                    </Col>

                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 ps-sm-2 ps-lg-4"
                    >
                        <ServiceCard
                            icon="painting-icon.svg"
                            alt="Paint roller"
                            title="Painting & decorating"
                        />
                    </Col>
                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 pe-sm-2 pe-lg-4"
                    >
                        <ServiceCard
                            icon="window-icon.svg"
                            alt="Window replacement"
                            title="Window supply & fitting"
                        />
                    </Col>
                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 ps-sm-2 ps-lg-2 pe-lg-3"
                    >
                        <ServiceCard
                            icon="electrical-icon.svg"
                            alt="Electric bolt with tools"
                            title="Electrical works"
                        />
                    </Col>

                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 pe-sm-2 pe-lg-2 ps-lg-3"
                    >
                        <ServiceCard
                            icon="sink-icon.svg"
                            alt="New kitchen"
                            title="Kitchens & bathrooms"
                        />
                    </Col>
                    <Col
                        xs={12}
                        sm={6}
                        lg={3}
                        className="px-0 mb-4 ps-sm-2 ps-lg-4"
                    >
                        <ServiceCard
                            icon="carpet-icon.svg"
                            alt="Carpet being rolled out"
                            title="Carpets & flooring"
                        />
                    </Col>
                </Row>
            </Container>
            <Container fluid="md" className="mt-2 px-3">
                <Row>
                    <Col className="text-center">
                        <Button
                            size="lg"
                            variant="warning"
                            onClick={() => {
                                dispatch(toggleMakeEnquiryModal(true))
                                dispatch(setMakeEnquiryReqType("general"))
                            }}
                            className="px-4 rounded-0"
                        >
                            <span className="text-bold text-white">
                                Make enquiry
                            </span>
                        </Button>
                    </Col>
                </Row>
            </Container>

            <Container
                fluid="md"
                as={Card}
                className="d-md-none mt-5 p-0 mb-6 pt-0"
            >
                <div className="mobile-services-div">
                    <CustomImage
                        src="https://webb-jenkins-static-164679926.imgix.net/services/builder-completing-roof-work.jpg"
                        alt="Builder stood proudly while admiring almost completed roofwork"
                        layout="fill"
                        objectPosition="center"
                    />
                </div>

                <Card.Body>
                    <Card.Title>
                        <h2>Why choose us?</h2>
                    </Card.Title>

                    <p>
                        We pride ourselves on the quality of our team of
                        experienced tradesmen who are courteous, reliable and
                        above all great at what they do. No matter how big or
                        small the task, we can connect you with an expert in the
                        field to complete it to an exceptional standard.
                    </p>
                    <p>
                        Dealing with tradesmen can be frustrating for any
                        landlord or home owner, but we coordinate the process on
                        your behalf from start to finish. We aim to make the
                        process as painless as possible, however we will always
                        seek your approval before having works carried out, that
                        way you will not receive any nasty surprises.
                    </p>
                    <p>
                        All of our tradesmen are local to the Isle of Wight,
                        meaning that we can get work tended to as quickly as
                        possible.
                    </p>
                </Card.Body>
            </Container>
            <Container fluid="md" className="d-none d-md-block mt-5 pb-0">
                <Row className="justify-content-end">
                    <div className="service-img-div">
                        <CustomImage
                            src="https://webb-jenkins-static-164679926.imgix.net/services/builder-completing-roof-work.jpg"
                            alt="Builder stood proudly while admiring almost completed roofwork"
                            layout="fill"
                            objectFit="cover"
                            objectPosition="center"
                            sizes="50vw"
                        />
                    </div>
                </Row>

                <Row className="service-txt-card">
                    <Card
                        as={Col}
                        xs={8}
                        className="bg-transparent p-0 shadow-none mt-auto"
                    >
                        <Waypoint onEnter={() => setInView(true)}>
                            <animated.div className="h-100" style={fadeLeft}>
                                <Card className="p-0">
                                    <Card.Body>
                                        <Card.Title>
                                            <h2>Why choose us?</h2>
                                        </Card.Title>

                                        <p>
                                            We pride ourselves on the quality of
                                            our team of experienced tradesmen
                                            who are courteous, reliable and
                                            above all great at what they do. No
                                            matter how big or small the task, we
                                            can connect you with an expert in
                                            the field to complete it to an
                                            exceptional standard.
                                        </p>
                                        <p>
                                            Dealing with tradesmen can be
                                            frustrating for any landlord or home
                                            owner, but we coordinate the process
                                            on your behalf from start to finish.
                                            We aim to make the process as
                                            painless as possible, however we
                                            will always seek your approval
                                            before having works carried out,
                                            that way you will not receive any
                                            nasty surprises.
                                        </p>
                                        <p>
                                            All of our tradesmen are local to
                                            the Isle of Wight, meaning that we
                                            can get work tended to as quickly as
                                            possible.
                                        </p>
                                    </Card.Body>
                                </Card>
                            </animated.div>
                        </Waypoint>
                    </Card>
                </Row>
            </Container>
        </>
    )
}

export default ServicesScreen
